import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { RacecraftReport } from '../../sim/bot/index.ts';
import { effectiveDials } from '../../sim/botProfile/index.ts';
import { PackHistoryDetail } from './packResult.tsx';
import { SketchModal } from './playtestLog.tsx';
import type { BotCatalogChoice, PlaytestHistoryEntry } from './workshop.tsx';

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatClock(ms: number): string {
  const seconds = ms / 1000;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${(seconds - minutes * 60).toFixed(1).padStart(4, '0')}`;
}

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

function packCrashes(report: RacecraftReport): number {
  return report.races.reduce((sum, race) => sum + race.crashes, 0);
}

/** A solo test's lines under the lead: Expert's time, races and their spread, crashes. */
function SoloDetail({
  entry,
  unfinished,
}: {
  entry: Extract<PlaytestHistoryEntry, { kind: 'solo' }>;
  unfinished: number;
}): JSX.Element {
  const { report } = entry;
  const races = report.races;
  const times = races.map((race) => race.timeMs / report.expertMs);
  const crashes = races.reduce((sum, race) => sum + race.crashes.length, 0);

  return (
    <>
      <span class="bot-history-detail">Expert {formatClock(report.expertMs)}</span>
      <span class="bot-history-detail">
        #{entry.number} · tested {formatTime(entry.finishedAt)} ·{' '}
        {report.sameEverySeed
          ? '1 race (same on every seed)'
          : `${races.length} races, ${percent(Math.min(...times))}–${percent(Math.max(...times))}`}{' '}
        · {crashes} {crashes === 1 ? 'crash' : 'crashes'}
        {unfinished > 0 ? ` · ${unfinished} unfinished` : ''}
      </span>
    </>
  );
}

/**
 * Every playtest of this bot this session, newest first, beside the chart. Each box leads with
 * the measured pace, then the bot's boat-clock on the map (the mean over its races); Expert's
 * time, when it ran, the spread, crashes, bars, and what has changed since sit smaller below. A
 * pack test is tagged Pack and lists its racecraft instead. Show course opens the test's map full size; clicking the box
 * shows its log under the chart; Restore puts its settings back (one undo step).
 */
export function PlaytestHistory({
  history,
  selectedId,
  maps,
  running,
  changesOf,
  onSelect,
  onRestore,
  onClear,
}: {
  history: readonly PlaytestHistoryEntry[];
  selectedId: number | null;
  maps: readonly BotCatalogChoice[];
  running: boolean;
  /** What differs between a test's bot and the bot now; empty when they are the same. */
  changesOf: (entry: PlaytestHistoryEntry) => string[];
  onSelect: (id: number) => void;
  onRestore: (id: number) => void;
  onClear: () => void;
}): JSX.Element {
  const [courseId, setCourseId] = useState<number | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const labelOf = (id: string) => maps.find((entry) => entry.id === id)?.label ?? id;
  const course = history.find((entry) => entry.id === courseId);

  return (
    <aside class="bot-history ui-panel ui-chrome" aria-label="Playtest history" data-bot-history>
      <div class="bot-history-head">
        <h2 class="bot-history-title">Playtest history</h2>
        {history.length > 0 ? (
          <button type="button" class="ui-button" onClick={onClear} disabled={running}>
            Clear
          </button>
        ) : null}
      </div>
      {history.length === 0 ? (
        <p class="ui-hint">
          Each quick or pack playtest lands here, so you can compare tests and go back to one.
        </p>
      ) : (
        <ol class="bot-history-list">
          {history.map((entry) => {
            const pack = entry.kind === 'pack';
            const pace = pack ? entry.report.summary.pace : entry.report.pace;
            const races: readonly { timeMs: number; finished: boolean }[] = entry.report.races;
            const meanMs = races.reduce((sum, race) => sum + race.timeMs, 0) / Math.max(1, races.length);
            const unfinished = races.filter((race) => !race.finished).length;
            const changes = changesOf(entry);
            const same = changes.length === 0;
            const dials = entry.profile.dials ? effectiveDials(entry.profile.dials) : null;
            const selected = entry.id === selectedId;

            return (
              <li
                key={entry.id}
                class={`bot-history-entry${selected ? ' is-selected' : ''}${same ? ' is-current' : ''}`}
                data-run={entry.number}
                data-run-kind={entry.kind}
                data-pace={pace.toFixed(3)}
              >
                <button
                  type="button"
                  class="bot-history-select"
                  aria-pressed={selected}
                  aria-label={`Show test ${entry.number}`}
                  disabled={running}
                  onClick={() => onSelect(entry.id)}
                >
                  <span class="bot-history-pace">
                    <strong>{percent(pace)}</strong>
                    <span class="bot-history-pace-of">of Expert’s time{pack ? ' in a pack' : ''}</span>
                    {pack ? <span class="bot-history-kind">Pack</span> : null}
                  </span>
                  <span class="bot-history-where">
                    <strong data-bot-history-clock>{formatClock(meanMs)}</strong>
                    {races.length > 1 ? ' avg' : ''} on {labelOf(entry.mapId)}
                  </span>
                  {entry.kind === 'pack' ? (
                    <>
                      <PackHistoryDetail report={entry.report} />
                      <span class="bot-history-detail">
                        #{entry.number} · tested {formatTime(entry.finishedAt)} · {races.length} races vs Expert ·{' '}
                        {packCrashes(entry.report)} {packCrashes(entry.report) === 1 ? 'crash' : 'crashes'}
                        {unfinished > 0 ? ` · ${unfinished} unfinished` : ''}
                      </span>
                    </>
                  ) : (
                    <SoloDetail entry={entry} unfinished={unfinished} />
                  )}
                  <span class="bot-history-dials">
                    {dials ? (
                      <>
                        <span title="Skill">S {dials.skill}</span>
                        <span title="Consistency">C {dials.consistency}</span>
                        <span title="Aggression">A {dials.aggression}</span>
                        <span title="Manners">M {dials.manners}</span>
                      </>
                    ) : (
                      <span>Custom</span>
                    )}
                  </span>
                  <span class="bot-history-changes">
                    {same ? 'Same settings as now' : `Changed since: ${changes.join(', ')}`}
                  </span>
                </button>
                <div class="bot-history-actions">
                  <button type="button" class="ui-button" onClick={() => setCourseId(entry.id)}>
                    Show course
                  </button>
                  <button
                    type="button"
                    class="ui-button"
                    disabled={same || running}
                    onClick={() => onRestore(entry.id)}
                  >
                    Restore these settings
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {course ? (
        <SketchModal
          sketch={course.sketch}
          races={course.kind === 'solo' ? course.report.races : []}
          focus={focus}
          onFocus={setFocus}
          onClose={() => setCourseId(null)}
          title={`Test #${course.number} · ${formatTime(course.finishedAt)} · ${labelOf(course.mapId)}`}
        />
      ) : null}
    </aside>
  );
}
